import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import {
  DEFAULT_LOCALE,
  LOCALES,
  LOCALE_LABELS,
  applyDocumentLang,
  isAppLocale,
  persistLocale,
  resolveLocale,
  type AppLocale,
} from './localePreference';

import enCommon from './locales/en-AU/common.json';
import enNav from './locales/en-AU/nav.json';
import enTenants from './locales/en-AU/tenants.json';
import enOnboarding from './locales/en-AU/onboarding.json';
import enDesigner from './locales/en-AU/designer.json';
import enRules from './locales/en-AU/rules.json';
import enCampaigns from './locales/en-AU/campaigns.json';
import enBilling from './locales/en-AU/billing.json';
import enAccess from './locales/en-AU/access.json';
import enPartners from './locales/en-AU/partners.json';
import enEnquiries from './locales/en-AU/enquiries.json';
import enTester from './locales/en-AU/tester.json';
import enUsers from './locales/en-AU/users.json';
import enGroups from './locales/en-AU/groups.json';

import esCommon from './locales/es/common.json';
import esNav from './locales/es/nav.json';
import esTenants from './locales/es/tenants.json';
import esOnboarding from './locales/es/onboarding.json';
import esDesigner from './locales/es/designer.json';
import esRules from './locales/es/rules.json';
import esCampaigns from './locales/es/campaigns.json';
import esBilling from './locales/es/billing.json';
import esAccess from './locales/es/access.json';
import esPartners from './locales/es/partners.json';
import esEnquiries from './locales/es/enquiries.json';
import esTester from './locales/es/tester.json';
import esUsers from './locales/es/users.json';
import esGroups from './locales/es/groups.json';

import frCommon from './locales/fr/common.json';
import frNav from './locales/fr/nav.json';
import frTenants from './locales/fr/tenants.json';
import frOnboarding from './locales/fr/onboarding.json';
import frDesigner from './locales/fr/designer.json';
import frRules from './locales/fr/rules.json';
import frCampaigns from './locales/fr/campaigns.json';
import frBilling from './locales/fr/billing.json';
import frAccess from './locales/fr/access.json';
import frPartners from './locales/fr/partners.json';
import frEnquiries from './locales/fr/enquiries.json';
import frTester from './locales/fr/tester.json';
import frUsers from './locales/fr/users.json';
import frGroups from './locales/fr/groups.json';

import deCommon from './locales/de/common.json';
import deNav from './locales/de/nav.json';
import deTenants from './locales/de/tenants.json';
import deOnboarding from './locales/de/onboarding.json';
import deDesigner from './locales/de/designer.json';
import deRules from './locales/de/rules.json';
import deCampaigns from './locales/de/campaigns.json';
import deBilling from './locales/de/billing.json';
import deAccess from './locales/de/access.json';
import dePartners from './locales/de/partners.json';
import deEnquiries from './locales/de/enquiries.json';
import deTester from './locales/de/tester.json';
import deUsers from './locales/de/users.json';
import deGroups from './locales/de/groups.json';

const NAMESPACES = [
  'common',
  'nav',
  'tenants',
  'onboarding',
  'designer',
  'rules',
  'campaigns',
  'billing',
  'access',
  'partners',
  'enquiries',
  'tester',
  'users',
  'groups',
] as const;

const resources = {
  'en-AU': {
    common: enCommon,
    nav: enNav,
    tenants: enTenants,
    onboarding: enOnboarding,
    designer: enDesigner,
    rules: enRules,
    campaigns: enCampaigns,
    billing: enBilling,
    access: enAccess,
    partners: enPartners,
    enquiries: enEnquiries,
    tester: enTester,
    users: enUsers,
    groups: enGroups,
  },
  es: {
    common: esCommon,
    nav: esNav,
    tenants: esTenants,
    onboarding: esOnboarding,
    designer: esDesigner,
    rules: esRules,
    campaigns: esCampaigns,
    billing: esBilling,
    access: esAccess,
    partners: esPartners,
    enquiries: esEnquiries,
    tester: esTester,
    users: esUsers,
    groups: esGroups,
  },
  fr: {
    common: frCommon,
    nav: frNav,
    tenants: frTenants,
    onboarding: frOnboarding,
    designer: frDesigner,
    rules: frRules,
    campaigns: frCampaigns,
    billing: frBilling,
    access: frAccess,
    partners: frPartners,
    enquiries: frEnquiries,
    tester: frTester,
    users: frUsers,
    groups: frGroups,
  },
  de: {
    common: deCommon,
    nav: deNav,
    tenants: deTenants,
    onboarding: deOnboarding,
    designer: deDesigner,
    rules: deRules,
    campaigns: deCampaigns,
    billing: deBilling,
    access: deAccess,
    partners: dePartners,
    enquiries: deEnquiries,
    tester: deTester,
    users: deUsers,
    groups: deGroups,
  },
} as const;

const initialLocale = resolveLocale();

void i18n.use(initReactI18next).init({
  resources,
  lng: initialLocale,
  fallbackLng: DEFAULT_LOCALE,
  supportedLngs: [...LOCALES],
  nonExplicitSupportedLngs: false,
  defaultNS: 'common',
  ns: [...NAMESPACES],
  interpolation: { escapeValue: false },
});

applyDocumentLang(initialLocale);

i18n.on('languageChanged', (lng) => {
  if (isAppLocale(lng)) {
    persistLocale(lng);
    applyDocumentLang(lng);
  }
});

export { i18n, NAMESPACES };
export type { AppLocale };
export {
  DEFAULT_LOCALE,
  LOCALES,
  LOCALE_LABELS,
  isAppLocale,
  persistLocale,
  resolveLocale,
};

export async function setAppLocale(locale: AppLocale): Promise<void> {
  persistLocale(locale);
  applyDocumentLang(locale);
  await i18n.changeLanguage(locale);
}
